import { useCallback, useMemo, useReducer } from 'react'
import { find, get, keyBy, map, round, toNumber } from 'lodash'

import Bus from '../bus'
import { orderBet } from '../api/orderBet'
import { useApplicationState, useBetslipStateUpdate } from '../state'

import { emptyBetslipState } from '../state/bet/betslip.constants'

import { logger } from '../utils'
import { BETSLIP_UPDATE_ERRORS } from '../actions'

const ORDER_ODDS_INPUT_RX = /^\d+\.?\d{0,2}$/m

const getInitialState = (orderedOdds) => ({
  orderedOdds: round(orderedOdds + 0.01, 2),
  orderedAmount: '',
  acceptSportServiceChange: false,
  acceptBetAmountChange: false
})

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_STATE': {
      const updatedState = action.payload

      return {
        ...state,
        ...updatedState
      }
    }

    default:
      return state
  }
}

export const useOrderBetslip = (betItems, selectedBet = {}) => {
  const applicationState = useApplicationState()
  const currency = get(applicationState, 'wallet.currency')
  const betslipStateUpdate = useBetslipStateUpdate()

  const initialState = useMemo(() => {
    if (!selectedBet || !selectedBet.odds) {
      return getInitialState(0)
    }
    return getInitialState(selectedBet.odds)
  }, [])

  const [state, dispatch] = useReducer(reducer, initialState)

  const {
    acceptBetAmountChange,
    acceptSportServiceChange,
    orderedAmount,
    orderedOdds
  } = state

  const setState = useCallback(payload => {
    dispatch({ type: 'SET_STATE', payload })
  }, [])

  const onOrderBet = async () => {
    // Switching betslip loading
    // Cleaning errors
    betslipStateUpdate({
      loading: true,
      errors: emptyBetslipState.errors,
      outcomesErrors: emptyBetslipState.outcomesErrors
    })

    try {
      const outcomeIds = map(betItems, ({ outcomeId }) => outcomeId)

      const orderResult = await orderBet({
        body: {
          orders: [{
            acceptBetAmountChange,
            acceptSportServiceChange,
            betAmount: orderedAmount,
            betCurrency: currency,
            minDesiredOdds: round(orderedOdds, 2),
            outcomeIds,
            skipInactiveOutcomes: false
          }]
        }
      })

      // FIXME: Rework data restructuring
      const bets = map(orderResult.createdOrders, order => ({
        ...order,
        betId: order.id,
        outcomes: map(order.outcomes, outcome => {
          const additionalOutcomeInfo = find(betItems, ({ outcomeId }) => (
            toNumber(outcomeId) === toNumber(outcome.outcomeId)
          ))

          return {
            ...outcome,
            outcomeInfo: {
              ...additionalOutcomeInfo,
              localizedEventName: outcome.eventName,
              outcomeName: outcome.outcomeName,
              marketName: outcome.marketName,
              eventId: outcome.eventId,
              marketId: outcome.marketId,
              betMarketGroups: additionalOutcomeInfo.betGroups
            },
            odds: outcome.currentOdds
          }
        })
      }))

      // Cleaning betslip bets & switching loading on success order bet
      betslipStateUpdate({
        loading: false,
        betItems: emptyBetslipState.betItems,
        betTypes: emptyBetslipState.betTypes
      })

      Bus.emit(Bus.events.bet.placedResult, { data: { bets } })
    } catch (data) {
      // TODO: double-check when BE will send errors in an appropriate way
      const { errors, message } = data
      const betslipNamespace = 'betslip:main'

      logger.error('[betslip:order] unable to do order bet:', message)

      // Betslip update errors action will set new errors
      // And will switch loading to false
      Bus.emit(betslipNamespace, {
        type: BETSLIP_UPDATE_ERRORS,
        payload: keyBy(errors, error => error.requestId || 'global')
      })
    }
  }

  const switchAcceptSportServiceChange = useCallback(() => {
    setState({ acceptSportServiceChange: !acceptSportServiceChange })
  }, [acceptSportServiceChange])

  const switchAcceptBetAmountChange = useCallback(() => {
    setState({ acceptBetAmountChange: !acceptBetAmountChange })
  }, [acceptBetAmountChange])

  const setOrderedOdds = useCallback((event) => {
    const { target: { value } } = event

    if (
      (value === '') || // On input clearing
      ORDER_ODDS_INPUT_RX.test(value)
    ) {
      setState({ orderedOdds: value })
    }
  }, [])

  const setOrderAmount = useCallback((orderedAmount) => {
    setState({ orderedAmount })
  }, [])

  return {
    ...state,
    setState,
    onOrderBet,
    switchAcceptSportServiceChange,
    switchAcceptBetAmountChange,
    setOrderedOdds,
    setOrderAmount
  }
}
